/* The archive scrubber, drawn as a printed bar chart.
 *
 * Discrete 3px bars with hard tops and amplitude quantised to eight heights.
 * That quantisation is the whole design: a real 2048-point peak render looks
 * like every audio library's default and belongs to none of this, while eight
 * steps reads as something that was SET rather than measured. The bars are the
 * same object as the level meter in the player bar.
 *
 * Progress is encoded in HEIGHT and COLOUR. Never opacity — a 40%-opacity bar
 * on a bone ground is a 40%-contrast bar, which fails on the exact screens it
 * was supposed to help.
 *
 * With no peaks it generates a DETERMINISTIC envelope seeded off the catalogue
 * number. Same episode, same shape, every render, every visitor, forever. A
 * random envelope that reshuffles on re-render is the single most broken-
 * looking thing a waveform can do, and it is what Math.random() gives you.
 *
 * The bars are aria-hidden. The real control is an <input type="range">
 * underneath them: it seeks with the arrow keys, announces its position, and
 * behaves the way its owner has configured their machine to behave. Nobody has
 * ever rebuilt that correctly out of divs.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

import { formatClock } from '../format';

/** 3px bar plus a 2px gap. The pitch the design is drawn on. */
const PITCH = 5;
const MIN_BARS = 24;
const MAX_BARS = 480;

/** Eight heights, and the floor is not zero — a silent bar is still a bar. */
const STEPS = 8;
const FLOOR = 0.12;

/** How tall an unplayed bar is drawn, as a fraction of its own amplitude. */
const UNPLAYED = 0.42;

function hashSeed(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** mulberry32. Small, fast, and identical on every machine — which is the point. */
function rng(seed: number): () => number {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function quantise(value: number): number {
  const clamped = Math.min(1, Math.max(0, value));
  return Math.max(FLOOR, Math.round(clamped * STEPS) / STEPS);
}

/**
 * The shape of a two-hour set: a quiet opening, a long climb, a plateau that
 * takes up most of it, and a tail. Seeded noise on top so no two catalogue
 * numbers look alike, but the same one always does.
 */
function synthesise(seed: string, count: number): number[] {
  const random = rng(hashSeed(seed || 'HGR'));
  const bars: number[] = new Array<number>(count);

  for (let i = 0; i < count; i += 1) {
    const x = count === 1 ? 0.5 : i / (count - 1);
    // Rises quickly, holds, drops off the end.
    const arc = Math.sin(Math.PI * Math.pow(x, 0.62));
    // Tuned so the peak plus both noise terms lands just under 1: an envelope
    // that clips spends half the set on the top step and stops being a shape.
    const body = 0.32 + 0.54 * arc;
    // Two noise scales: a slow drift for sections, a fast one for bars.
    const drift = 0.12 * Math.sin(x * 11 + random() * 0.001);
    const jitter = (random() - 0.5) * 0.26;
    bars[i] = quantise(body + drift + jitter);
  }

  return bars;
}

/** Real peaks, bucketed down to the number of bars that fit. Max per bucket. */
function resample(peaks: readonly number[], count: number): number[] {
  if (peaks.length === 0) return [];
  const largest = peaks.reduce((max, p) => (p > max ? p : max), 0) || 1;
  const bars: number[] = new Array<number>(count);

  for (let i = 0; i < count; i += 1) {
    const from = Math.floor((i * peaks.length) / count);
    const to = Math.max(from + 1, Math.floor(((i + 1) * peaks.length) / count));
    let peak = 0;
    for (let j = from; j < to && j < peaks.length; j += 1) {
      const value = Math.abs(peaks[j]);
      if (value > peak) peak = value;
    }
    bars[i] = quantise(peak / largest);
  }

  return bars;
}

export interface WaveformProps {
  /**
   * Amplitudes, any scale — they are normalised against their own maximum.
   * Leave it out and the envelope is generated from `seed`.
   */
  peaks?: readonly number[] | null;
  /** The seed for a generated envelope. The catalogue number, always. */
  seed: string;
  /** Where playback is now. */
  positionSeconds: number;
  /** The whole set. 0 or unknown makes the waveform read-only. */
  durationSeconds: number;
  /** Given, the waveform is seekable — by keyboard, by pointer, by drag. */
  onSeek?: (seconds: number) => void;
  /** Bar height in px. The episode block draws 56, the player bar 28. */
  height?: number;
  /** Names the control. "Seek within Sunday Service" reads better than "Seek". */
  label?: string;
  className?: string;
}

export function Waveform({
  peaks,
  seed,
  positionSeconds,
  durationSeconds,
  onSeek,
  height = 56,
  label = 'Position in this set',
  className,
}: WaveformProps) {
  const barsRef = useRef<HTMLDivElement | null>(null);
  const [count, setCount] = useState(96);

  /* How many bars actually fit. Measured, not guessed: the same component is
     420px wide in the player bar and 1380px wide on the episode page, and a
     fixed count would either overflow one or leave the other half empty. */
  useEffect(() => {
    const node = barsRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;

    const measure = (width: number) => {
      if (width <= 0) return;
      const next = Math.min(MAX_BARS, Math.max(MIN_BARS, Math.floor(width / PITCH)));
      setCount((current) => (current === next ? current : next));
    };

    measure(node.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) measure(entry.contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const amplitudes = useMemo(
    () => (peaks && peaks.length > 0 ? resample(peaks, count) : synthesise(seed, count)),
    [peaks, seed, count],
  );

  const duration = Number.isFinite(durationSeconds) && durationSeconds > 0 ? durationSeconds : 0;
  const position = Math.min(duration, Math.max(0, positionSeconds || 0));
  const played = duration > 0 ? position / duration : 0;
  const seekable = Boolean(onSeek) && duration > 0;

  /* Pointer seeking on the bars themselves — "drag anywhere on the bars",
     as drawn. It is an enhancement on top of the range input, never instead of
     it, which is why the bars stay aria-hidden. */
  const seekFromClientX = useCallback(
    (clientX: number) => {
      const node = barsRef.current;
      if (!node || !onSeek || duration <= 0) return;
      const box = node.getBoundingClientRect();
      if (box.width <= 0) return;
      const fraction = Math.min(1, Math.max(0, (clientX - box.left) / box.width));
      onSeek(fraction * duration);
    },
    [onSeek, duration],
  );

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!seekable) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    seekFromClientX(event.clientX);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!seekable || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
    seekFromClientX(event.clientX);
  };

  const cut = Math.round(played * count);

  return (
    <div className={className ? `waveblock ${className}` : 'waveblock'}>
      <div
        className="wave"
        ref={barsRef}
        style={{ height: `${height}px` }}
        aria-hidden="true"
        data-seekable={seekable ? 'true' : undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
      >
        {amplitudes.map((amplitude, index) => {
          const isPlayed = index < cut;
          const scale = isPlayed ? 1 : UNPLAYED;
          return (
            <i
              key={index}
              className={isPlayed ? 'wave__bar wave__bar--played' : 'wave__bar'}
              style={{ height: `${Math.max(3, Math.round(amplitude * scale * height))}px` }}
            />
          );
        })}
      </div>

      {seekable ? (
        <input
          className="wave__range"
          type="range"
          min={0}
          max={Math.round(duration)}
          step={1}
          value={Math.round(position)}
          aria-label={label}
          aria-valuetext={`${formatClock(position)} of ${formatClock(duration)}`}
          onChange={(event) => onSeek?.(Number(event.target.value))}
        />
      ) : (
        /* Not seekable — a live stream, or a set whose length we do not know.
           A disabled range is a control nobody can focus and nobody can read,
           so this is a progressbar and says so. */
        <div
          className="wave__static"
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={duration > 0 ? Math.round(duration) : undefined}
          aria-valuenow={duration > 0 ? Math.round(position) : undefined}
          aria-valuetext={
            duration > 0
              ? `${formatClock(position)} of ${formatClock(duration)}`
              : `${formatClock(position)} elapsed`
          }
        >
          <span className="wave__staticfill" style={{ width: `${played * 100}%` }} />
        </div>
      )}
    </div>
  );
}
